import { createSelector } from 'reselect';
import { decode } from 'base-64';
import isEmpty from 'lodash/isEmpty';

import {
  formatErrorMessages,
  getQueryParam,
  validateBase64String,
} from '../../../utils/globalHelpers';
import {
  GUIDED_VIEW_NAMESPACE_DICTIONARY,
  REGISTER_SUCCESS_MESSAGE,
  REGISTER_VIEW_NAMESPACE_DICTIONARY,
} from './constants';
import { initialState } from './reducer';
import { validate } from '../../../utils/validate';
import makeSelectMobile from '../../Mobile/selectors';
import {
  CONSENT_PAGE_HEADER_DEFAULT,
  CONSENT_SECONDARY_BUTTON_LABEL,
  CONSENT_SUBMIT_LABEL,
  FORM_CONSENT,
  STATUS_CONSENT,
} from '../../Main/constants';
import { selectMainStatus } from '../../Main/selectors';

/**
 * Direct selector to the Register state domain
 */
const selectRegisterDomain = (state) => state.register || initialState;

/**
 * Direct selector to Register props
 */
const selectRegisterProps = (state, props) => props;

/**
 * Other specific selectors
 */

const getRegisterConfirmationPageProps = () => createSelector(
  makeSelectRegister('error'),
  getRegisterFormErrors('account'),
  getRegisterIsGuided(),
  getRegisterIsVerify(),
  makeSelectRegister('status'),
  (
    errors,
    formErrors,
    isGuidedRegistration,
    isVerifyRegistration,
    status,
  ) => {
    const { username: usernameError } = formErrors;
    let errorOrSuccess = '';
    let message = '';
    let resetStep = 1;

    if (isVerifyRegistration) {
      if (status.verify === 'succeeded') {
        errorOrSuccess = 'verifySuccess';
      } else {
        errorOrSuccess = 'verifyError';
        if (errors.verify) {
          message = errors.verify;
        }
      }
      return { errorOrSuccess, message, resetStep };
    }
    if (isGuidedRegistration && status.guidedStepFour === 'succeeded') {
      errorOrSuccess = 'verifySuccess';
    } else if ((!isGuidedRegistration && status.stepTwo === 'succeeded') ||
      (status.registerResendEmail === 'succeeded')) {
      errorOrSuccess = 'success';
      message = REGISTER_SUCCESS_MESSAGE;
    } else {
      const error = Object.entries(errors).find(([, value]) => !!value);
      if (error) {
        const [errorKey, errorValue] = error;
        if (errorKey === 'stepTwoError' && usernameError) {
          errorOrSuccess = 'stepTwoValidationError';
          resetStep = 6;
        } else if (errorKey === 'registerConfirmation') {
          errorOrSuccess = 'registerChangeEmailError';
          resetStep = 9;
          return { errorOrSuccess, message: errorValue.message, resetStep };
        } else {
          errorOrSuccess = errorKey;
        }
        message = errorValue;
      }
    }
    return { errorOrSuccess, message, resetStep };
  },
);

/**
 * @description Selects props for the consent form for register flow
 */
const getRegisterConsentFormProps = () => createSelector(
  getRegisterFormErrors(FORM_CONSENT),
  selectMainStatus(STATUS_CONSENT),
  getRegisterForm(FORM_CONSENT),
  (
    error,
    status,
    formValues,
  ) => ({
    error: error || '',
    formName: FORM_CONSENT,
    formValues,
    pageHeader: CONSENT_PAGE_HEADER_DEFAULT,
    secondaryButtonLabel: CONSENT_SECONDARY_BUTTON_LABEL,
    status,
    submitLabel: CONSENT_SUBMIT_LABEL,
  }),
);

/**
 * @description Selects url view param to determine registration process
 */
const getRegisterIsGuided = () => createSelector(
  getRegisterPageView(),
  (view) => view === 'init',
);

const getRegisterIsVerify = () => createSelector(
  getRegisterPageView(),
  (view) => view === 'verify',
);

const getRegisterError = (prop) => createSelector(
  makeSelectRegister('error'),
  (error) => error[prop],
);

/**
 * @description Selects register form inputs depending on viewNamespace
 */
const getRegisterForm = (prop) => createSelector(
  makeSelectRegister('form'),
  getRegisterViewNamespace(),
  (form, viewNamespace) => form[prop || viewNamespace],
);

/**
 * @description Selects errors for register form depending on viewNamespace
 */
const getRegisterFormErrors = (prop) => createSelector(
  makeSelectRegister('formErrors'),
  getRegisterViewNamespace(),
  (formErrors, viewNamespace) => formErrors[prop || viewNamespace],
);

/**
 * @description Selects register form inputs depending on viewNamespace
 */
const getRegisterResendChangeEmail = () => createSelector(
  makeSelectRegister('form'),
  (form) => form.account.isChangeEmail,
);

/**
 * @description Selects current email from after attempting login without verifying
 */
const getRegisterCurrentEmail = () => createSelector(
  makeSelectRegister('form'),
  (form) => form.account.currEmail,
);

/**
 * @description Selects errors and formatted form input errors for screen reader
 */
const getRegisterNotifications = () => createSelector(
  getRegisterError('bannerError'),
  getRegisterError('guidedStepFourError'),
  getRegisterError('serviceError'),
  getRegisterError('stepOneError'),
  getRegisterError('stepTwoError'),
  getRegisterError('verify'),
  getRegisterFormErrors('account'),
  getRegisterFormErrors('initial'),
  (
    bannerError,
    guidedStepFourError,
    serviceError,
    stepOneError,
    stepTwoError,
    verifyError,
    accountErrors,
    initialErrors,
  ) => {
    const nonFormErrors = [
      bannerError,
      guidedStepFourError,
      serviceError,
      stepOneError,
      stepTwoError,
      verifyError,
    ];
    const notifications = nonFormErrors.filter((error) => !!error);
    return notifications.concat(formatErrorMessages({ ...accountErrors, ...initialErrors }));
  },
);

const getRegisterPageView = () => createSelector(
  makeSelectMobile('isMobile'),
  selectRegisterProps,
  (isMobile, props) => {
    if (isMobile) {
      const { route: { params: { view } = {} } } = props;
      return view || 'main';
    }
    return props.match.params.view || 'main';
  },
);

const getRegisterLoanNumberQueryParam = () => createSelector(
  makeSelectMobile('isMobile'),
  selectRegisterProps,
  (isMobile, { route }) => {
    let encodedLoanNumber;
    if (isMobile) {
      const { params: { ln } = {} } = route;
      encodedLoanNumber = ln;
    } else {
      encodedLoanNumber = getQueryParam('ln', null, false);
    }
    if (encodedLoanNumber && validateBase64String(encodedLoanNumber)) {
      const value = decode(encodedLoanNumber);
      if (isEmpty(validate({ type: 'loanNumber', value }))) {
        return value;
      }
    }
    return '';
  },
);

const getRegisterQueryString = () => createSelector(
  makeSelectMobile('isMobile'),
  selectRegisterProps,
  (isMobile, { route }) => {
    if (isMobile) {
      const { params: { q } = {} } = route;
      return encodeURIComponent(q);
    }
    return getQueryParam('q', null, false);
  },
);

const getRegisterStatusProp = (prop) => createSelector(
  makeSelectRegister('status'),
  (status) => status[prop],
);

const getRegisterViewNamespace = () => createSelector(
  getRegisterIsGuided(),
  makeSelectRegister('step'),
  (isGuided, step) => isGuided ?
    GUIDED_VIEW_NAMESPACE_DICTIONARY[step] :
    REGISTER_VIEW_NAMESPACE_DICTIONARY[step],
);

/**
 * Default selector used by Register
 */
const makeSelectRegister = (prop) => createSelector(
  selectRegisterDomain,
  (state) => state[prop],
);

export default makeSelectRegister;
export {
  getRegisterConfirmationPageProps,
  getRegisterConsentFormProps,
  getRegisterCurrentEmail,
  getRegisterError,
  getRegisterForm,
  getRegisterFormErrors,
  getRegisterIsGuided,
  getRegisterIsVerify,
  getRegisterLoanNumberQueryParam,
  getRegisterNotifications,
  getRegisterPageView,
  getRegisterQueryString,
  getRegisterResendChangeEmail,
  getRegisterStatusProp,
  getRegisterViewNamespace,
};
